/**
 * Web3Auth Gateway Implementation
 *
 * Handles Web3Auth SDK operations for embedded wallet provisioning.
 * Uses custom JWT authentication with Cognito.
 *
 * IMPORTANT: This class requires Web3Auth context from Web3AuthProvider.
 * It must be used within React components that have access to Web3Auth hooks.
 *
 * For dependency injection with Redux store, use useWeb3AuthGateway hook.
 *
 * @see docs/stories/3.10.c.web3auth-embedded-wallet-frontend.story.md
 * @see docs/research/web3auth-vs-privy-analysis.md
 */

import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { WALLET_ADAPTERS, type IProvider } from "@web3auth/base";
import { LOGIN_PROVIDER, type LoginParams } from "@web3auth/auth";
import type { IWeb3AuthGateway } from "@domain/ports/IWeb3AuthGateway";
import type { EmbeddedWallet } from "@domain/types/EmbeddedWallet";
import {
  getAllWalletAddresses,
  PrivateKeyExportDisabledError,
} from "@/lib/walletDerivation";

/**
 * Custom auth connection ID configured in Web3Auth Dashboard
 *
 * NOTE: For custom JWT authentication, Web3Auth v9+ uses the
 * LOGIN_PROVIDER.JWT login provider. The verifier ID and JWKS
 * endpoint are configured in Web3Auth Dashboard under Custom Connections.
 *
 * Dashboard configuration:
 * - Custom Connection ID: anyw3ar-cognito
 * - JWKS Endpoint: https://cognito-idp.{region}.amazonaws.com/{userPoolId}/.well-known/jwks.json
 * - JWT user identifier: "email"
 */

/**
 * Web3Auth context type from useWeb3Auth hook
 */
type Web3AuthContext = ReturnType<typeof useWeb3Auth>;

/**
 * Web3Auth Gateway Implementation
 *
 * Handles Web3Auth SDK operations for embedded wallet provisioning.
 * Uses custom JWT authentication with Cognito.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const gateway = useWeb3AuthGateway();
 *
 *   const handleLogin = async () => {
 *     await gateway.authenticateWithCognito(idToken);
 *     const wallets = await gateway.getEmbeddedWallets();
 *   };
 * }
 * ```
 */
export class Web3AuthGateway implements IWeb3AuthGateway {
  constructor(private readonly context: Web3AuthContext) {}

  /**
   * Check if Web3Auth gateway is available
   * Returns true when Web3Auth context is properly initialized
   */
  isAvailable(): boolean {
    return !!this.context.web3Auth;
  }

  /**
   * Authenticate with Web3Auth using Cognito ID token
   *
   * Uses custom JWT connection with Cognito JWKS validation.
   * The custom auth connection must be configured in Web3Auth Dashboard:
   * - JWKS Endpoint: https://cognito-idp.{region}.amazonaws.com/{userPoolId}/.well-known/jwks.json
   * - JWT user identifier: "email" or "custom:userId"
   *
   * @param idToken - Cognito ID token (JWT)
   * @throws Error if authentication fails (invalid token, network error)
   */
  async authenticateWithCognito(idToken: string): Promise<void> {
    if (this.isAuthenticated()) {
      console.log("[Web3AuthGateway] Already authenticated, skipping");
      return;
    }

    if (!this.context.web3Auth) {
      throw new Error(
        "Web3Auth not initialized. Ensure Web3AuthProvider is configured."
      );
    }

    try {
      // Configure login params for custom JWT authentication
      const loginParams: LoginParams = {
        loginProvider: LOGIN_PROVIDER.JWT,
        extraLoginOptions: {
          id_token_hint: idToken,
          verifierIdField: "email",
          // Custom connection domain (optional, depends on dashboard config)
          // domain: `https://cognito-idp.{region}.amazonaws.com/{userPoolId}`,
        },
      };

      // Connect using the AUTH adapter with custom JWT
      await this.context.web3Auth.connectTo(WALLET_ADAPTERS.AUTH, loginParams);
    } catch (error) {
      console.error("[Web3AuthGateway] Authentication failed:", error);
      throw new Error(
        `Web3Auth authentication failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Get embedded wallets derived from Web3Auth provider
   *
   * Derives ETH, POLYGON (same address), and SOL wallets.
   *
   * @returns Array of 3 embedded wallets
   * @throws Error if not authenticated
   * @throws PrivateKeyExportDisabledError if key export is disabled in dashboard
   */
  async getEmbeddedWallets(): Promise<EmbeddedWallet[]> {
    if (!this.isAuthenticated()) {
      throw new Error("Not authenticated with Web3Auth");
    }

    const provider = this.context.provider;
    if (!provider) {
      throw new Error("Web3Auth provider not available");
    }

    try {
      const addresses = await getAllWalletAddresses(provider as IProvider);

      return [
        { chain: "ETH", address: addresses.eth, source: "web3auth" },
        { chain: "POLYGON", address: addresses.polygon, source: "web3auth" },
        { chain: "SOL", address: addresses.sol, source: "web3auth" },
      ];
    } catch (error) {
      if (error instanceof PrivateKeyExportDisabledError) {
        throw error;
      }
      console.error("[Web3AuthGateway] Failed to get wallets:", error);
      throw new Error(
        `Failed to derive wallets: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Check if currently authenticated with Web3Auth
   */
  isAuthenticated(): boolean {
    return this.context.isConnected ?? false;
  }

  /**
   * Disconnect from Web3Auth session
   */
  async disconnect(): Promise<void> {
    if (!this.isAuthenticated()) {
      return;
    }

    try {
      await this.context.logout();
    } catch (error) {
      console.error("[Web3AuthGateway] Disconnect failed:", error);
      throw new Error(
        `Web3Auth disconnect failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }
}

/**
 * Disabled gateway for when Web3Auth is not configured
 *
 * Returns safe no-op implementations for all methods.
 * isAvailable() returns false, allowing callers to skip operations.
 */
class DisabledWeb3AuthGateway implements IWeb3AuthGateway {
  isAvailable(): boolean {
    return false;
  }

  async authenticateWithCognito(_idToken: string): Promise<void> {
    console.warn("[DisabledWeb3AuthGateway] Web3Auth not configured");
  }

  async getEmbeddedWallets(): Promise<EmbeddedWallet[]> {
    return [];
  }

  isAuthenticated(): boolean {
    return false;
  }

  async disconnect(): Promise<void> {
    // No-op
  }
}

// Singleton disabled gateway instance
const disabledGateway = new DisabledWeb3AuthGateway();

// Check if Web3Auth is configured at module level
const isWeb3AuthEnabled = !!import.meta.env.VITE_WEB3AUTH_CLIENT_ID;

/**
 * Hook to create Web3AuthGateway instance from React hooks
 *
 * This hook must be used within a component that has Web3Auth context.
 * The returned gateway instance can be passed to Redux thunks.
 *
 * When VITE_WEB3AUTH_CLIENT_ID is not set, returns a disabled gateway
 * that is safe to use without Web3Auth context.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const gateway = useWeb3AuthGateway();
 *
 *   if (!gateway.isAvailable()) {
 *     // Web3Auth not configured, skip wallet operations
 *     return;
 *   }
 *
 *   const handleLogin = async () => {
 *     await gateway.authenticateWithCognito(idToken);
 *     const wallets = await gateway.getEmbeddedWallets();
 *   };
 * }
 * ```
 */
export function useWeb3AuthGateway(): IWeb3AuthGateway {
  // Return disabled gateway when Web3Auth is not configured
  // This avoids calling useWeb3Auth() which requires the provider
  if (!isWeb3AuthEnabled) {
    return disabledGateway;
  }

  // Safe to call useWeb3Auth() when provider is configured
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const context = useWeb3Auth();
  return new Web3AuthGateway(context);
}
